import React from 'react';
import { Mail, Plus, Trash2, RefreshCw } from 'lucide-react';
import { useGmailAccounts } from '../../hooks/useGmailAccounts';
import LoadingSpinner from '../ui/LoadingSpinner';
import ConnectGmailButton from './ConnectGmailButton';
import { formatDistanceToNow } from 'date-fns';

export default function GmailIntegration() {
  const { accounts, loading, error, disconnect, refreshToken, refreshing } = useGmailAccounts();

  const handleRefresh = async (accountId: string) => {
    try {
      await refreshToken(accountId);
    } catch (err) {
      console.error('Failed to refresh token:', err);
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading Gmail accounts..." />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Connected Gmail Accounts</h2>
        <ConnectGmailButton />
      </div>

      {error && (
        <div className="text-red-500 text-sm">{error}</div>
      )}

      <div className="space-y-4">
        {accounts.map((account) => (
          <div key={account.id} className="card p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-primary-500" />
              <div>
                <p className="font-medium">{account.email}</p>
                <p className="text-sm text-gray-400">
                  Connected {new Date(account.created_at).toLocaleDateString()} •{' '}
                  Token expires {formatDistanceToNow(new Date(account.token_expiry))}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleRefresh(account.id)}
                disabled={refreshing}
                className="text-gray-400 hover:text-primary-500 disabled:opacity-50"
                title="Refresh token"
              >
                <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={() => disconnect(account.id)}
                className="text-gray-400 hover:text-red-500"
                title="Disconnect account"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          </div>
        ))}

        {accounts.length === 0 && (
          <div className="text-center py-8">
            <Mail className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-400">No Gmail accounts connected</p>
            <p className="text-sm text-gray-500 mt-2">
              Connect your Gmail accounts to import and sync communications
            </p>
          </div>
        )}
      </div>
    </div>
  );
}